import { definedMap } from "@glideapps/ts-necessities";
import {
    asArrayValue,
    asBoolean,
    asEnum,
    asString,
    deconstructLatLong,
    isLoadedDefined,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { ActionSource } from "common/components/computation-types";
import { asUI, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer, MapListUI } from "common/components/uis";
import { MapVisualType } from "generator/array-screens/map-screen-types";
import { MapListMode } from "generator/components/map-list-mode";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import * as React from "react";
import EmptyWrapper from "webapp/ui/views/empty-wrapper/empty-wrapper";
import List from "../ui/smart/list/list";
import MapList from "../ui/smart/map-list/map-list";
import type { MapMarker } from "../ui/smart/map-list/map-list-types";
import { HighlightGrowContext } from "./highlight-component-material-renderer";

export const MapListMaterialRenderer: GlideFunctionalRenderer<MapListUI> = p => {
    const { env, getValueProducers, ctx, ui } = p;
    const [force] = useForce();
    const [
        [
            list,
            markers,
            itemUI,
            searchActive,
            defaultMode,
            insideInlineList,
            showUserLocationPV,
            emptyMessage,
            onTap,
            visualTypePV,
        ],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClickWithCustomContext: onClick } = useOnClickFromAction(onTap, ActionSource.MapScreen, ctx, env);

    const setGrow = React.useContext(HighlightGrowContext);
    setGrow("always");

    if (list === LOADING || markers === LOADING || itemUI === LOADING || insideInlineList === LOADING) {
        return null;
    }

    const visualType = loadedDefinedMap(visualTypePV, v => asEnum<MapVisualType>(v)) ?? MapVisualType.Street;
    const isSearchActive = loadedDefinedMapWithDefault(searchActive, false, asBoolean);
    const isInsideInlineList = asBoolean(insideInlineList);

    const markerList: MapMarker[] = [];
    const markersArray = asArrayValue(markers);
    const arr = asArrayValue(list);
    let markersAccountedFor = 0;

    for (let index = 0; index < markersArray.length; index++) {
        const itemVP = markersArray[index];
        const listItemVP = arr[index];

        const inspector = itemVP.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) continue;

        const locationVP = inspector.getProperty("location");
        if (locationVP === undefined) continue;

        const latLong = deconstructLatLong(locationVP, force);
        if (latLong === LOADING) continue;

        markersAccountedFor++;
        if (latLong === undefined) continue;

        const title = loadedDefinedMap(definedMap(inspector.getProperty("title"), force), asString) ?? "";

        const itemID = loadedDefinedMap(definedMap(inspector.getProperty("itemID"), force), asString) ?? "";

        markerList.push({ title, lat: latLong.latitude, lng: latLong.longitude, handle: listItemVP, itemID });
    }
    const ignoreDefaultModeForTablet = env.sizeClass === "tablet" ? MapListMode.Map : defaultMode;
    const mode = loadedDefinedMapWithDefault(ignoreDefaultModeForTablet, MapListMode.Map, asString);
    const showUserLocation = loadedDefinedMapWithDefault(showUserLocationPV, false, asBoolean);
    const emptyMessageValue = loadedDefinedMap(emptyMessage, asString);

    const factory = (marker: MapMarker) => {
        return asUI(itemUI).render(marker.handle, env, undefined);
    };

    const onMarkerTapped =
        onClick !== undefined
            ? (marker: MapMarker) => {
                  if (!(env.sizeClass === "tablet")) return false;
                  void onClick(marker.handle, undefined);
                  return true;
              }
            : undefined;

    const saveState = (state: any) => {
        ui.state = state;
    };

    if (arr.length === 0 && markerList.length === 0) {
        if (emptyMessage === undefined) return null;
        return <EmptyWrapper>{emptyMessage}</EmptyWrapper>;
    }

    const listUI = (
        <List
            appFeatures={env.appEnvironment.appFeatures}
            eminenceFlags={env.appEnvironment.eminenceFlags}
            title=""
            hasUnevenRows={true}
            items={arr}
            topAlign={false}
            emptyMessage={emptyMessageValue}>
            {(item, i, columnWidth) => asUI(itemUI).render(item, env.with({ columnWidth }), i)}
        </List>
    );

    return (
        <MapList
            renderEnvironment={env}
            isSearchActive={isSearchActive}
            listCount={arr.length}
            visualType={visualType}
            listContent={listUI}
            markers={markerList}
            markersLoading={markersAccountedFor !== arr.length}
            itemFactory={factory}
            saveState={saveState}
            savedState={ui.state}
            defaultMode={mode as MapListMode}
            insideInlineList={isInsideInlineList}
            showUserLocation={showUserLocation}
            onMarkerTapped={onMarkerTapped}
        />
    );
};
